import { Plus, X } from 'lucide-react';
import type { CustomFieldSchema, DomainRule } from '@contracts';

const OPS: { value: string; label: string }[] = [
  { value: '=', label: '=' },
  { value: '!=', label: '≠' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '≥' },
  { value: '<=', label: '≤' },
  { value: 'ilike', label: 'contiene' },
];

export function DomainBuilder({
  fields,
  domain,
  onChange,
}: {
  fields: CustomFieldSchema[];
  domain: DomainRule[];
  onChange: (d: DomainRule[]) => void;
}) {
  const add = () => onChange([...domain, { field: fields[0]?.name ?? '', op: '=', value: '' }]);
  const update = (i: number, patch: Partial<DomainRule>) =>
    onChange(domain.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(domain.filter((_, j) => j !== i));

  const sel = 'rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-red-500';

  return (
    <div className="space-y-2">
      {domain.length === 0 && <p className="text-xs text-slate-400">Sin filtros — toda la tabla.</p>}
      {domain.map((r, i) => {
        const field = fields.find((f) => f.name === r.field);
        const t = field?.type;
        return (
          <div key={i} className="flex items-center gap-1.5">
            {/* al cambiar de campo se limpia el valor (puede cambiar el tipo de input) */}
            <select value={r.field} onChange={(e) => update(i, { field: e.target.value, value: '' })} className={`${sel} flex-1`}>
              {fields.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.label}
                </option>
              ))}
            </select>
            <select value={r.op} onChange={(e) => update(i, { op: e.target.value })} className={sel}>
              {OPS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {t === 'selection' && field?.options ? (
              <select value={String(r.value ?? '')} onChange={(e) => update(i, { value: e.target.value })} className={`${sel} w-36`}>
                <option value="">—</option>
                {field.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : t === 'date' || t === 'datetime' ? (
              <input
                type="date"
                value={String(r.value ?? '')}
                onChange={(e) => update(i, { value: e.target.value })}
                className={`${sel} w-36`}
              />
            ) : t === 'bool' ? (
              <select
                value={r.value === true ? 'true' : r.value === false ? 'false' : ''}
                onChange={(e) => update(i, { value: e.target.value === 'true' })}
                className={`${sel} w-28`}
              >
                <option value="">—</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            ) : (
              <input
                type={t === 'number' ? 'number' : 'text'}
                value={String(r.value ?? '')}
                onChange={(e) => update(i, { value: t === 'number' ? Number(e.target.value) : e.target.value })}
                placeholder="valor"
                className={`${sel} w-28`}
              />
            )}
            <button
              onClick={() => remove(i)}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      <button
        onClick={add}
        disabled={fields.length === 0}
        className="flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:border-red-400 hover:text-red-600 disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" /> Agregar filtro
      </button>
    </div>
  );
}
